import React, { useState, useEffect, useMemo } from "react";
import ReactModal from "react-modal";
import ChangeUserModal from "./ticketListInfo/ChangeUserModal";
import { useChangeStatusQuery } from "../../Services/settingsAPIs/CommonAPI";
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import ChangeStatusModal from "./ChangeStatusModal";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  useUpdateDispositionMutation,
  useUpdateTicketWorkflowMutation,
  useMarkTicketActionableMutation,
} from "../../Services/ticket/CreateTicket";
import {
  capitalizeAndFormat,
  capitalizeFirstLetter,
} from "../reusableComponent/CapitalLetter";
import { format } from "date-fns";
import DispositionTicketModal from "./DispositionTicketModal";

import { toast } from "react-toastify";
import {
  getBadgeClasses,
  getIconClasses,
  getPriorityFlagColor,
} from "../reusableComponent/IconAndBadgeClasses";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BadgeIcon from "@mui/icons-material/Badge";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SLMetricsTicketTimer from "./SLMetricsTicketTimer";
import ConfirmationModal from "../reusableComponent/NonActionableConfirmationModal";

import "./TicketListInfoCard.scss";

const isMobile = window.innerWidth < 600;

const customModalStyles: any = {
  content: {
    top: isMobile ? "0" : "50%",
    left: isMobile ? "0" : "50%",
    right: "auto",
    bottom: "auto",
    transform: isMobile ? "none" : "translate(-50%, -50%)",
    width: isMobile ? "100%" : "100%",
    maxWidth: isMobile ? "100%" : "550px",
    height: "auto",
    maxHeight: isMobile ? "600px" : "700px",
    overflowY: "auto",
    padding: "20px",
    borderRadius: isMobile ? "0" : "12px",
    display: "block",
  },
};

const isEscalationLevelOne = (level: string | undefined): boolean => {
  if (!level) return false;
  const match = String(level).match(/escalation\s*level\s*(\d+)/i);
  return match ? Number(match[1]) === 1 : false;
};

const getLogsFromLatestEscalationLevelOne = (
  logs: any[] | null | undefined,
): any[] => {
  if (!logs?.length) return [];

  let lastLevelOneIndex = -1;
  logs.forEach((log, index) => {
    if (isEscalationLevelOne(log?.escalation_level)) {
      lastLevelOneIndex = index;
    }
  });

  if (lastLevelOneIndex <= 0) return [...logs];
  return logs.slice(lastLevelOneIndex);
};

const TicketListInfoCard = ({
  tickets,
  refetch,
  refetchAuditTrailData,
  setGetAllStatus,
  refetchAuditTrailForwardedData,
  openChildTicketModal,
  dependancyValue,
  storeLabel,
}) => {
  const [isAddNewContactModalOpen, setAddNewContactModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [originalStatus, setOriginalStatus] = useState("");
  const [selectedAllStatus, setAllSelectedStatus] = useState("");
  const [isStatusModalOpen, setStatusModalOpen] = useState(false);
  const [defaultSelect, setDefaultSelect] = useState("");
  const [storingTicketId, setStoringTicketId] = useState("");
  const [openDispositionModal, setOpenDispositionModal] = useState(false);
  const [storedDisposition, setStoredDisposition] = useState([]);

  // Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const navigate = useNavigate();


  const fetchingTenant = window.location.hostname.split(".")[0];
  const storedTicketId = "";

  const { data, refetch: refetchAuditTrail } = useChangeStatusQuery(
    tickets?.action?.disposition?.id,
    {
      skip: !tickets?.action?.disposition?.id,
    },
  );

  const [trigger, { isLoading }] = useUpdateDispositionMutation();
  const [triggerUpdateTicket] = useUpdateTicketWorkflowMutation();
  const [triggerMarkActionable] = useMarkTicketActionableMutation();
  const [nonActionablePending, setNonActionablePending] = useState(false);

  // Actual API call — runs only after user confirms
  const handleNonActionableToggle = async () => {
    if (!tickets?.id) return;
    setNonActionablePending(true);
    try {
      const response = tickets.is_non_actionable
        ? await triggerMarkActionable(tickets.id)
        : await triggerUpdateTicket({
            id: tickets.id,
            body: { is_non_actionable: !tickets.is_non_actionable },
          });
      if (response?.data) {
        refetch();
        toast.success(
          tickets.is_non_actionable
            ? "Ticket marked as actionable"
            : "Ticket marked as non-actionable",
        );
      } else if (response?.error) {
        toast.error("Unable to update non-actionable status");
      }
    } catch {
      toast.error("An error occurred while updating non-actionable status");
    } finally {
      setNonActionablePending(false);
    }
  };

  useEffect(() => {
    if (tickets?.action?.disposition?.disposition) {
      setStoredDisposition(tickets?.action?.disposition?.disposition);
    }
  }, [tickets?.action?.disposition?.disposition]);

  useEffect(() => {
    setDefaultSelect(tickets?.status_display || "");
  }, [tickets]);

  const openContactModal = () => setAddNewContactModalOpen(true);
  const closeContactModal = () => setAddNewContactModalOpen(false);
  const openDispositionModall = () => setOpenDispositionModal(true);
  const closeDispositionModall = () => setOpenDispositionModal(false);

  const openStatusModal = () => {
    setOriginalStatus(selectedStatus);
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setSelectedStatus(originalStatus);
    setStatusModalOpen(false);
  };

  const handleStatusUpdate = () => setStatusModalOpen(false);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "active":
        return "active";
      case "closed":
        return "closed";
      case "reopen":
        return "reopen";
      case "wip":
        return "wip";
      default:
        return "";
    }
  };

  const formatStatusData = (status) => {
    if (typeof status.status === "string") {
      return { status_type: status.status_type, status: status.status };
    }
    return {
      id: status?.status?.id ? status?.status?.id : "",
      status_type: status?.status_type,
      status: status?.status?.status,
    };
  };

  const handlerStatus = (allStatus) => {
    const tenantName = window.location.hostname.split(".")[0]?.toLowerCase();
    const dependencyField = storeLabel?.find((el) =>
      el?.label?.toLowerCase().includes("dependency"),
    );
    const dependencyValue = dependencyField?.value ?? dependancyValue;

    if (
      ["payswiff"].includes(tenantName) &&
      ["wip", "pending"].includes(allStatus.status_type?.toLowerCase()) &&
      !dependencyValue
    ) {
      toast.warning("Please fill Dependency before selecting WIP or Pending.");
      return;
    }

    setAllSelectedStatus(allStatus);
    setSelectedStatus(allStatus.status_type);
    openStatusModal();
    refetchAuditTrailForwardedData();
  };

  const statusOptions = useMemo(() => {
    const mapped =
      data?.all_statuses?.map((status) => formatStatusData(status)) ?? [];

    const tenantFiltered = mapped.filter(
      (status) =>
        !(
          ["payswiff", "payswiffibl"].includes(fetchingTenant) &&
          status.status_type?.toLowerCase() === "reopen"
        ),
    );

    const closedLabels = new Set<string>();
    for (const status of data?.all_statuses ?? []) {
      if (String(status?.status_type ?? "").toLowerCase() !== "closed") continue;
      if (typeof status.status === "string") {
        const lab = String(status.status)
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();
        if (lab) closedLabels.add(lab);
      } else if (status?.status?.status) {
        const lab = String(status.status.status)
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();
        if (lab) closedLabels.add(lab);
      }
    }

    const isTicketInClosedStage =
      String(tickets?.status_type ?? "").toLowerCase() === "closed" ||
      (() => {
        const display = String(tickets?.status_display ?? "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();
        return Boolean(display && closedLabels.has(display));
      })();

    const optionIsReopen = (s: any) => {
      if (String(s?.status_type ?? "").toLowerCase() === "reopen") return true;
      const label = String(s?.status ?? "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
      return label === "reopen" || label === "re-open";
    };

    const includeReopenRow = (s: any) => {
      if (!optionIsReopen(s)) return true;
      return String(tickets?.status_type ?? "").toLowerCase() === "reopen";
    };

    if (isTicketInClosedStage) return tenantFiltered;
    return tenantFiltered.filter((s) => includeReopenRow(s));
  }, [
    data?.all_statuses,
    tickets?.status_type,
    tickets?.status_display,
    fetchingTenant,
  ]);

  useEffect(() => {
    setGetAllStatus(data);
  }, [data, setGetAllStatus]);

  const truncateDescription = (description, maxLength) => {
    if (description?.length > maxLength)
      return description?.slice(0, maxLength) + "...";
    return description;
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 120 },
    },
  };

  const handleNavigate = () => {
    navigate(`/ticket/information/${tickets?.parent_ticket_id}`);
    window.location.reload();
  };

  // Determine if the ticket is already non-actionable (locked state)
  const isAlreadyNonActionable = tickets?.is_non_actionable === true;
  const normalizedSlaBreachLogs = useMemo(
    () => getLogsFromLatestEscalationLevelOne(tickets?.sla_breach_logs),
    [tickets?.sla_breach_logs],
  );

  return (
    <div className="ticket-info-card">
      <div className="ticket-header">
        <div className="header-top">
          <span className="ticket-id">{tickets?.id}</span>
          <h3 className="ticket-title">{tickets?.title}</h3>

          {/* Channel badge */}
          <div
            className={`channel-badge-wrapper ${getBadgeClasses(
              tickets?.ticket_channel,
            )}`}
            title={tickets?.ticket_channel}
          >
            <i className={`${getIconClasses(tickets?.ticket_channel)}`}></i>
          </div>

          <Tooltip
            title={
              isAlreadyNonActionable
                ? "Click to mark as actionable"
                : "Click to mark as non-actionable"
            }
            arrow
            placement="top"
          >
            {/* Wrapper span required so Tooltip works on a disabled button */}
            <span
              style={{
                display: "inline-flex",
                cursor: "pointer",
              }}
            >
              <button
                onClick={() => setConfirmModalOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "3px 10px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: 600,
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                  pointerEvents: "auto",
                  cursor: "pointer",
                  border: "1px solid #d1d5db",
                        backgroundColor: "transparent",
                        color: "#6b7280",
                        opacity: nonActionablePending ? 0.7 : 1,
                  // ── Disabled / non-actionable state → muted secondary slate ──
                 
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#fef9c3";
                  e.currentTarget.style.borderColor = "#d97706";
                  e.currentTarget.style.color = "#92400e";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#d1d5db";
                  e.currentTarget.style.color = "#6b7280";
                }}
              >
                {nonActionablePending ? (
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      border: "2px solid currentColor",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.6s linear infinite",
                    }}
                  />
                ) : (
                  <i
                    className={
                      isAlreadyNonActionable
                        ? "bi bi-ticket-perforated-fill"
                        : "bi bi-ticket-perforated"
                    }
                    style={{
                      // muted slate icon when disabled, normal grey otherwise
                      color: isAlreadyNonActionable ? "#94a3b8" : "#6B7280",
                      fontSize: "14px",
                    }}
                  />
                )}
                <span>
                  {isAlreadyNonActionable
                    ? "Mark Actionable"
                    : "Mark Non-Actionable"}
                </span>
              </button>
            </span>
          </Tooltip>
     {normalizedSlaBreachLogs.length > 0 && (
            <div className="sl-timer-row">
              <SLMetricsTicketTimer
                tagBasedSlBreach={normalizedSlaBreachLogs}
                statusType={tickets?.status_type}
                otherMailDetails={tickets}
              />
            </div>
          )}
          <span className="ticket-date">{tickets?.createdAt}</span>
        </div>

        <div className="header-bottom">
         
            <span
              className={`status-badge-ticket ${getStatusStyle(tickets?.status_type)}`}
              title={tickets?.status_display}
            >
              {truncateDescription(tickets?.status_display, 25)}
            </span>

            <span className="ticket-description" title={tickets?.description}>
              {truncateDescription(tickets?.description, 120)}
            </span>

            <div className="ticket-actions">
              <div className="assignee-section">
                <span className="assignee-name">
                  Assignee:{" "}
                  {capitalizeAndFormat(
                    tickets?.action?.assignee?.username.split("@")[0] ??
                      "Unassigned",
                  )}
                </span>
                <KeyboardArrowDownIcon
                  className="dropdown-icon"
                  onClick={openContactModal}
                  sx={{ fontSize: 35 }}
                />
              </div>

              <VisibilityIcon
                className="disposition-action-icon"
                onClick={openDispositionModall}
                sx={{ fontSize: 35 }}
                titleAccess="View disposition"
              />

              {tickets?.is_child_ticket === false && (
                <i
                  className="fas fa-sitemap cursor-pointer text-gray-600"
                  style={{ fontSize: "15px" }}
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Create child ticket"
                  onClick={openChildTicketModal}
                ></i>
              )}

              {tickets?.is_child_ticket === true && (
                <BadgeIcon
                  className="action-icon"
                  onClick={handleNavigate}
                  sx={{ fontSize: 35 }}
                  titleAccess="View parent ticket"
                />
              )}
            </div>
          </div>

     
        
      </div>

      <div className="ticket-details-bar">
        {tickets?.action?.thread ? (
          <Link
            to={`/email/inbox/${tickets?.action?.thread[0]?.id}`}
            className="detail-item email-action"
            data-bs-toggle="tooltip"
            title="Process Email"
          >
            <EmailIcon className="email-icon" />
          </Link>
        ) : (
          <Link
            to="/email/compose"
            className="detail-item email-action"
            data-bs-toggle="tooltip"
            title="Compose Email"
          >
            <i className="text-dark bi bi-pencil-square fs-2"></i>
          </Link>
        )}

        <div className="detail-item">
          <span className="detail-label">Assignee</span>
          <span className="detail-value">
            {tickets?.action?.assignee?.username.split("@")[0] || "Unassigned"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Customer Name</span>
          <span className="detail-value">
            {tickets?.action?.contact?.name
              ? capitalizeFirstLetter(tickets?.action?.contact.name)
              : "-"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Raised By</span>
          <span className="detail-value">
          {tickets?.created_by?.username || tickets?.created_by || "-"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Priority</span>
          <span
            className={`priority-value ${
              tickets?.action?.disposition?.priority?.toLowerCase() || ""
            }`}
            style={{
              color: tickets?.action?.disposition?.priority
                ? getPriorityFlagColor(tickets?.action?.disposition?.priority)
                : "#666666",
            }}
            title={
              tickets?.action?.disposition?.priority
                ? `${capitalizeFirstLetter(
                    tickets?.action?.disposition?.priority,
                  )} Priority`
                : ""
            }
          >
            {tickets?.action?.disposition?.priority
              ? capitalizeFirstLetter(tickets?.action?.disposition?.priority)
              : "-"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Workflow</span>
          <span className="detail-value">
            {tickets?.action?.disposition?.workflow?.name || "-"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Created At</span>
          <span className="detail-value">
            {tickets?.created
              ? format(new Date(tickets.created), "dd-MM-yyyy HH:mm:ss")
              : "-"}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Resolved At</span>
          <span className="detail-value">
            {tickets?.resolved_at
              ? format(new Date(tickets?.resolved_at), "dd-MM-yyyy HH:mm:ss")
              : "-"}
          </span>
        </div>
     

        <div className="detail-item">
          <span className="detail-label">Current Team</span>
          <span className="detail-value">
           {tickets?.team_assignee?.name || tickets?.team_name || "-"}
          </span>
        </div>

        <div className="detail-item status-select-item">
          <span className="detail-label">Change Status</span>
          <FormControl className="status-select-control">
            <Select
              labelId="status-select-label"
              id="status-select"
              value={defaultSelect}
              MenuProps={MenuProps}
              displayEmpty
            >
              {statusOptions?.map((status) => (
                <MenuItem
                  key={
                    status.id ??
                    `${status.status_type}-${status.status}`
                  }
                  value={status.status}
                  onClick={() => handlerStatus(status)}
                  title={status?.status}
                >
                  {truncateDescription(status?.status, 25)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleNonActionableToggle}
        title="Confirm Action"
        message={
          isAlreadyNonActionable ? (
            <>
              Are you sure you want to{" "}
              <strong>mark this ticket as actionable</strong>?
            </>
          ) : (
            <>
              Are you sure you want to{" "}
              <strong>mark this ticket as non-actionable</strong>?
            </>
          )
        }
      />

      <ReactModal
        isOpen={isAddNewContactModalOpen}
        onRequestClose={closeContactModal}
        style={customModalStyles}
        contentLabel="New Contact"
        shouldCloseOnOverlayClick={false}
      >
        <ChangeUserModal
          closeModal={closeContactModal}
          refetch={refetch}
          refetchAuditTrailForwardedData={refetchAuditTrailForwardedData}
          userChangeType="Ticket"
          refetchEmailTrail={""}
        />
      </ReactModal>

      <ReactModal
        isOpen={openDispositionModal}
        onRequestClose={closeDispositionModall}
        style={customModalStyles}
        contentLabel="New Contact"
        shouldCloseOnOverlayClick={false}
      >
        <DispositionTicketModal
          isLoading={isLoading}
          trigger={trigger}
          closeModal={closeDispositionModall}
          storedDisposition={storedDisposition}
          refetch={refetch}
          ticketId={storedTicketId}
          value={""}
        />
      </ReactModal>

      <ReactModal
        isOpen={isStatusModalOpen}
        onRequestClose={closeStatusModal}
        style={customModalStyles}
        contentLabel="Change Status"
        shouldCloseOnOverlayClick={false}
      >
        <ChangeStatusModal
          data={data}
          closeStatusModal={closeStatusModal}
          refetch={refetch}
          refetchAuditTrail={refetchAuditTrail}
          selectedStatus={selectedStatus}
          handleStatusUpdate={handleStatusUpdate}
          refetchAuditTrailData={refetchAuditTrailData}
          selectedAllStatus={selectedAllStatus}
          storingTicketId={storingTicketId}
          refetchAuditTrailForwardedData={refetchAuditTrailForwardedData}
        />
      </ReactModal>
    </div>
  );
};

export default TicketListInfoCard;